import React, { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { ClockClockwiseIcon, ArrowClockwiseIcon } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "-";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function BacktestLog() {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/backtest-attempts?limit=200")
    .then((r) => setAttempts(r.data.attempts || []))
    .catch((e) => console.error(e))
    .finally(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const newCount = attempts.filter((a) => a.change === "new").length;
  const updCount = attempts.filter((a) => a.change === "updated").length;
  const familySet = new Set(attempts.map((a) => a.strategy_family).filter(Boolean));
  const symbolSet = new Set(attempts.map((a) => a.symbol).filter(Boolean));

  return (
    <div data-testid="backtest-log-page" className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">Pipeline Health</div>
          <h1 className="text-xl md:text-2xl tracking-tight font-medium mt-1 text-[#F1F5F9] flex items-center gap-2">
            <ClockClockwiseIcon size={22} className="text-[#D6FF38]" weight="duotone" />
            Backtest Attempt Log
          </h1>
        </div>
        <button
          data-testid="backtest-log-refresh"
          onClick={load}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-[#2A2D3A] rounded text-xs mono uppercase tracking-widest text-[#94A3B8] hover:text-[#F1F5F9]"
        >
          <ArrowClockwiseIcon size={12} /> refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Recorded" value={attempts.length} sub="last 200 backtests" />
        <StatTile label="New" value={newCount} tone="#22C55E" sub="first-seen bots" />
        <StatTile label="Updated" value={updCount} tone="#60A5FA" sub="metrics moved" />
        <StatTile
          label="Symbols · Families"
          value={`${symbolSet.size} · ${familySet.size}`}
          sub="unique in window"
        />
      </div>

      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md overflow-hidden">
        <div className="p-4 border-b border-[#2A2D3A] flex items-center justify-between">
          <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">
            Most recent activity (rolling window)
          </div>
          {loading && (
            <span className="text-[11px] mono text-[#64748B]">loading…</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="backtest-log-table">
            <thead>
              <tr className="text-[10px] mono uppercase tracking-widest text-[#64748B] border-b border-[#2A2D3A]">
                <th className="text-left py-2.5 px-4 font-normal">When</th>
                <th className="text-left py-2.5 px-4 font-normal">Bot</th>
                <th className="text-left py-2.5 px-4 font-normal">Symbol</th>
                <th className="text-left py-2.5 px-4 font-normal">Family</th>
                <th className="text-left py-2.5 px-4 font-normal">Status</th>
                <th className="text-right py-2.5 px-4 font-normal">Growth %</th>
                <th className="text-right py-2.5 px-4 font-normal">Months</th>
                <th className="text-right py-2.5 px-4 font-normal">Trades</th>
                <th className="text-left py-2.5 px-4 font-normal">Δ</th>
              </tr>
            </thead>
            <tbody>
              {attempts.length === 0 && !loading && (
                <tr>
                  <td colSpan="9" className="py-10 text-center text-xs mono text-[#64748B]">
                    no backtest deltas recorded yet — bridge pushes will populate this log
                  </td>
                </tr>
              )}
              {attempts.map((a, i) => (
                <tr
                  key={`${a.bot}-${a.observed_at}-${i}`}
                  className="border-b border-[#2A2D3A]/40 hover:bg-white/[0.02] transition-colors"
                  data-testid={`backtest-log-row-${i}`}
                >
                  <td className="py-2 px-4 mono text-[11px] text-[#94A3B8]">
                    <span title={a.observed_at}>{timeAgo(a.observed_at)} ago</span>
                  </td>
                  <td className="py-2 px-4 mono text-[#F1F5F9]" title={a.bot}>
                    {a.bot.length > 34 ? `${a.bot.slice(0, 14)}…${a.bot.slice(-14)}` : a.bot}
                  </td>
                  <td className="py-2 px-4 mono text-[#94A3B8]">{a.symbol || "-"}</td>
                  <td className="py-2 px-4 mono text-[#94A3B8]">{a.strategy_family || "-"}</td>
                  <td className="py-2 px-4"><StatusBadge status={a.status} /></td>
                  <td
                    className="py-2 px-4 mono text-right"
                    style={{ color: (a.growth_pct ?? 0) >= 0 ? "#2ECA7F" : "#FF4D4D" }}
                  >
                    {fmt.signed(a.growth_pct)}%
                  </td>
                  <td className="py-2 px-4 mono text-right text-[#94A3B8]">{a.months_tested}</td>
                  <td className="py-2 px-4 mono text-right text-[#94A3B8]">{a.total_trades}</td>
                  <td className="py-2 px-4">
                    <span
                      className="text-[10px] mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={{
                        color: a.change === "new" ? "#22C55E" : "#60A5FA",
                        background: a.change === "new" ? "rgba(34,197,94,0.10)" : "rgba(96,165,250,0.10)",
                        border: `1px solid ${a.change === "new" ? "rgba(34,197,94,0.30)" : "rgba(96,165,250,0.30)"}`,
                      }}
                    >
                      {a.change}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, tone = "#F1F5F9" }) {
  return (
    <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-3">
      <div className="text-[10px] mono uppercase tracking-widest text-[#94A3B8]">{label}</div>
      <div className="mono text-2xl font-medium tracking-tight mt-1" style={{ color: tone }}>{value}</div>
      {sub && <div className="text-[10px] mono text-[#64748B] mt-1">{sub}</div>}
    </div>
  );
}
